import React from 'react';
import { Button, Input, MultiSelect, Select } from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';

const ORG_WIDE = '__org__';

/**
 * The API stores one assignment per role+property pair; the editor works on rows
 * of { roleId, scopeIds (ORG_WIDE or property ids), approvalLimitAmount }.
 * Groups flat assignments into rows (same role + limit merge into one row).
 */
export function rowsFromAssignments(assignments) {
  const rows = [];
  const byKey = new Map();
  for (const a of assignments || []) {
    if (a.propertyId == null) {
      rows.push({ roleId: a.roleId, scopeIds: [ORG_WIDE], approvalLimitAmount: a.approvalLimitAmount ?? null });
      continue;
    }
    const key = `${a.roleId}|${a.approvalLimitAmount ?? ''}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.scopeIds.push(a.propertyId);
    } else {
      const row = { roleId: a.roleId, scopeIds: [a.propertyId], approvalLimitAmount: a.approvalLimitAmount ?? null };
      byKey.set(key, row);
      rows.push(row);
    }
  }
  return rows;
}

/** Expands editor rows back into the flat one-per-property assignments the API expects. */
export function assignmentsFromRows(rows) {
  const out = [];
  for (const r of rows || []) {
    const limit = r.approvalLimitAmount ?? null;
    if (r.scopeIds.includes(ORG_WIDE)) {
      out.push({ roleId: r.roleId, propertyId: null, approvalLimitAmount: limit, approvalLimitCurrency: null });
    } else {
      for (const pid of r.scopeIds) {
        out.push({ roleId: r.roleId, propertyId: pid, approvalLimitAmount: limit, approvalLimitCurrency: null });
      }
    }
  }
  return out;
}

/**
 * Returns an error message when any row is incomplete, otherwise null.
 * Messages are English dictionary keys — translate with t(...) where displayed.
 */
export function validateRows(rows) {
  if (!rows || rows.length === 0) return 'Assign at least one role.';
  for (const r of rows) {
    if (!r.roleId) return 'Choose a role for every assignment.';
    if (r.scopeIds.length === 0) return 'Choose a scope (organization-wide or at least one property) for every role.';
  }
  return null;
}

export default function RoleAssignmentsEditor({ value, onChange, roles, properties }) {
  const t = useT();
  const roleOptions = roles.map((r) => ({ value: r.id, label: r.name }));
  const scopeOptions = [
    { value: ORG_WIDE, label: t('Organization-wide (all properties)') },
    ...properties.map((p) => ({ value: p.id, label: p.name })),
  ];

  const update = (idx, patch) => {
    const next = value.map((a, i) => (i === idx ? { ...a, ...patch } : a));
    onChange(next);
  };

  // Org-wide is exclusive: picking it clears the property list, and picking a
  // property drops org-wide.
  const setScope = (idx, next) => {
    const prev = value[idx].scopeIds;
    let sel = next;
    if (sel.includes(ORG_WIDE)) {
      if (!prev.includes(ORG_WIDE)) sel = [ORG_WIDE];
      else if (sel.length > 1) sel = sel.filter((v) => v !== ORG_WIDE);
    }
    update(idx, { scopeIds: sel });
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  const add = () => {
    onChange([
      ...value,
      {
        roleId: roles[0]?.id ?? '',
        scopeIds: [ORG_WIDE],
        approvalLimitAmount: null,
      },
    ]);
  };

  return (
    <div className="usr-assignments">
      {value.length === 0 && (
        <p className="usr-assignments__empty">{t('No roles assigned yet. Add at least one role.')}</p>
      )}

      {value.map((a, idx) => (
        <div className="usr-assignment" key={idx}>
          <div className="usr-assignment__row">
            <div className="ps-field usr-assignment__grow">
              <span className="sp-field-label">{t('Role')}</span>
              <Select
                value={a.roleId}
                onChange={(v) => update(idx, { roleId: v })}
                options={roleOptions}
                ariaLabel={t('Role')}
                searchable
              />
            </div>
            <button
              type="button"
              className="usr-assignment__remove"
              onClick={() => remove(idx)}
              aria-label={t('Remove assignment')}
              title={t('Remove')}
            >
              <Icon.Trash size={16} />
            </button>
          </div>
          <div className="usr-assignment__row">
            <div className="ps-field usr-assignment__grow">
              <span className="sp-field-label">{t('Scope')}</span>
              <MultiSelect
                value={a.scopeIds}
                onChange={(v) => setScope(idx, v)}
                options={scopeOptions}
                placeholder={t('Choose properties…')}
                ariaLabel={t('Scope')}
              />
            </div>
          </div>
          <div className="usr-assignment__row">
            <div className="ps-field usr-assignment__grow">
              <Input
                type="number"
                label={t("Approval limit (optional, in the property's currency)")}
                value={a.approvalLimitAmount ?? ''}
                onChange={(e) =>
                  update(idx, {
                    approvalLimitAmount: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                placeholder={t('No limit')}
              />
            </div>
          </div>
        </div>
      ))}

      <Button type="button" variant="ghost" size="sm" leadingIcon={<Icon.Plus size={15} />} onClick={add}>
        {t('Add role assignment')}
      </Button>
    </div>
  );
}
